import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useConfirm } from "../lib/confirm";
import { Plus, Edit2, Trash2, X, Mail, FileDown } from "lucide-react";
import Icon3D from "../components/Icon3D";

const EVENT_TYPES = [
  "Regular Lodge Meeting",
  "Initiation",
  "Degree Conferral Ceremony",
  "Installation of Officers",
  "Annual Charity Banquet",
  "Social Gathering",
  "Educational Lecture",
  "Grand Lodge Communication",
  "Charity Event",
  "Internal Notices",
];

const emptyForm = {
  title: "",
  description: "",
  date: "",
  location: "",
  event_type: "Regular Lodge Meeting",
  invited_members: [],
  visibility: "lodge",
  visible_lodge_ids: [],
};

export default function EventsPage() {
  const { t, i18n } = useTranslation();
  const locale =t("events.en_gb");
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const confirm = useConfirm();
  const [events, setEvents] = useState([]);
  const [members, setMembers] = useState([]);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [childLodges, setChildLodges] = useState([]);

  const isObedienceAdmin = !!user && !user.child_lodge_id && user.role === "admin";

  // Can the current admin manage (edit/delete) this event?
  // Mirrors the backend permission check in `update_event` / `delete_event`:
  //   - superadmin: anywhere
  //   - obedience admin (no child_lodge_id): anywhere within their obedience tree
  //   - child-lodge admin / impersonating obedience admin: only events whose
  //     `child_lodge_id` matches their active scope
  const canManageEvent = (ev) => {
    if (!user) return false;
    if (user.role === "superadmin") return true;
    if (user.role !== "admin") return false;
    if (user.child_lodge_id) {
      return ev.child_lodge_id === user.child_lodge_id;
    }
    return true;
  };

  const load = async () => {
    try {
      const eventsReq = isObedienceAdmin
        ? api.get("/events", { params: { include_children: true } })
        : api.get("/events");
      const childReq = isAdmin
        ? api.get("/jurisdiction-lodges").catch(() => ({ data: [] }))
        : Promise.resolve({ data: [] });
      const [e, m, c] = await Promise.all([eventsReq, api.get("/members"), childReq]);
      setEvents(e.data);
      setMembers(m.data);
      setChildLodges(c.data || []);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (ev) => {
    setEditing(ev.id);
    const dt = ev.date ? new Date(ev.date) : null;
    const local = dt ? new Date(dt.getTime() - dt.getTimezoneOffset() * 60000).toISOString().slice(0, 16) : "";
    setForm({ ...emptyForm, ...ev, visibility: ev.visibility || "lodge", visible_lodge_ids: ev.visible_lodge_ids || [], date: local });
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = { ...form, date: new Date(form.date).toISOString() };
      if (editing) await api.put(`/events/${editing}`, payload);
      else await api.post("/events", payload);
      setShowForm(false);
      toast.success(editing ? t("events.updatedSuccess") : t("events.createdSuccess"));
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (ev) => {
    const ok = await confirm({ title: t("common.delete"), message: t("events.deleteConfirm") });
    if (!ok) return;
    try {
      await api.delete(`/events/${ev.id}`);
      toast.success(t("events.deletedSuccess"));
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const downloadInvitation = async (ev) => {
    setDownloadingId(ev.id);
    try {
      const resp = await api.get(`/events/${ev.id}/invitation.pdf`, {
        params: { lang: i18n.language, translate: false },
        responseType: "blob",
        timeout: 30000,
      });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      let filename = `invitatie_${(ev.title || "event").replace(/[^a-zA-Z0-9 _-]/g, "")}.pdf`;
      const cd = resp.headers?.["content-disposition"] || resp.headers?.["Content-Disposition"];
      if (cd) {
        const star = cd.match(/filename\*=UTF-8''([^;]+)/i);
        const plain = cd.match(/filename="?([^";]+)"?/i);
        try {
          if (star && star[1]) filename = decodeURIComponent(star[1].trim());
          else if (plain && plain[1]) filename = plain[1].trim();
        } catch (_e) {
          /* keep fallback */
        }
      }
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("events.invitationReady"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDownloadingId(null);
    }
  };

  const [sendingInviteId, setSendingInviteId] = useState(null);

  const sendInvite = async (ev) => {
    const ok = window.confirm(t("events.confirmSendInvite", { count: ev.invited_members?.length || 0 }));
    if (!ok) return;
    setSendingInviteId(ev.id);
    try {
      const { data } = await api.post(`/events/${ev.id}/send-invite`, {});
      if (data.recipients_count === 0) {
        toast.error(t("events.no_invited_members_with_email"));
        return;
      }
      if (data.sent > 0) {
        toast.success(t("events.inviteSent", { count: data.sent }));
      } else {
        toast.error(t("events.inviteSendFailed"));
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSendingInviteId(null);
    }
  };

  const toggleMember = (mid) => {
    setForm((f) => ({
      ...f,
      invited_members: f.invited_members.includes(mid)
        ? f.invited_members.filter((x) => x !== mid)
        : [...f.invited_members, mid],
    }));
  };

  const now = new Date().toISOString();
  const upcoming = events.filter((e) => e.date >= now);
  const past = events.filter((e) => e.date < now);

  const renderEvent = (ev) => {
    const d = new Date(ev.date);
    const monthLbl = d.toLocaleDateString(locale, { month: "short" }).replace(".", "").toUpperCase();
    const dayLbl = String(d.getDate()).padStart(2, "0");
    const timeLbl = d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
    const agendaPreview = ev.description
      ? ev.description.split(/\r?\n/)[0].slice(0, 90) + (ev.description.length > 90 ? "…" : "")
      : null;
    // Resolve the originating sub-lodge name when the event belongs to a child lodge
    // (visible to obedience admins who opted into `include_children`).
    const childLodge = ev.child_lodge_id
      ? childLodges.find((c) => c.id === ev.child_lodge_id)
      : null;
    return (
      <div key={ev.id} className="event-row" data-testid={`event-${ev.id}`}>
        <div className="event-row__date">
          <span className="event-row__month">{monthLbl}</span>
          <span className="event-row__day">{dayLbl}</span>
          <span className="event-row__time">{timeLbl}</span>
        </div>

        <div className="event-row__body">
          <div className="event-row__title">{ev.title}</div>
          <div className="event-row__meta">
            <span className="badge-grade">{t(`events.types.${ev.event_type}`, ev.event_type)}</span>
            {childLodge && (
              <span
                className="badge-grade"
                style={{ background: "rgba(201,168,76,0.18)", color: "#9a7530", borderColor: "rgba(201,168,76,0.5)" }}
                data-testid={`event-child-lodge-${ev.id}`}
                title={t("events.organizedBySublodge", "Organized by sub-lodge")}
              >
                {childLodge.name}{childLodge.number ? ` · ${childLodge.number}` : ""}
              </span>
            )}
            {ev.location && (
              <span className="event-row__location">
                <Icon3D name="pin" size={18} alt="" />
                {ev.location}
              </span>
            )}
            {ev.invited_members?.length > 0 && (
              <span style={{ fontSize: "0.82rem", fontStyle: "italic", color: "#858481" }}>
                {ev.invited_members.length} {t("events.invited")}
              </span>
            )}
          </div>
          {agendaPreview && (
            <div className="event-row__agenda">
              <strong>{t("events.agenda")}</strong>
              {agendaPreview}
            </div>
          )}
        </div>

        {isAdmin && (
          <div className="event-row__actions">
            <button
              className="btn-outline-gold flex items-center gap-1"
              onClick={() => downloadInvitation(ev)}
              data-testid={`invitation-pdf-${ev.id}`}
              title={t("events.downloadInvitation")}
              disabled={downloadingId === ev.id}
            >
              <FileDown size={12} />
              <span className="hidden sm:inline" style={{ marginLeft: 6 }}>
                {downloadingId === ev.id ? t("events.downloadingInvitation") : t("events.downloadInvitation")}
              </span>
            </button>
            {ev.invited_members?.length > 0 && !ev.child_lodge_id && (
              <button
                className="btn-outline-gold flex items-center gap-1"
                onClick={() => sendInvite(ev)}
                data-testid={`invite-event-${ev.id}`}
                title={t("events.send_invitation")}
                disabled={sendingInviteId === ev.id}
              >
                <Mail size={12} />
                {sendingInviteId === ev.id && <span style={{ marginLeft: 4 }}>…</span>}
              </button>
            )}
            {/* Edit/Delete: enabled when the current admin is permitted to
                manage this event per the backend rules (see `canManageEvent`).
                The previous gate hid the buttons for any event with a
                `child_lodge_id`, which also locked out the sub-lodge's own
                admin from editing their own events. */}
            {canManageEvent(ev) && (
              <>
                <button
                  className="btn-outline-gold"
                  onClick={() => openEdit(ev)}
                  data-testid={`edit-event-${ev.id}`}
                  title={t("common.edit", "EDIT")}
                >
                  <Edit2 size={12} />
                  <span className="hidden sm:inline" style={{ marginLeft: 6 }}>{t("common.edit", "EDIT")}</span>
                </button>
                <button
                  className="btn-danger"
                  onClick={() => remove(ev)}
                  data-testid={`delete-event-${ev.id}`}
                  title={t("common.delete", "DELETE")}
                >
                  <Trash2 size={12} />
                  <span className="hidden sm:inline" style={{ marginLeft: 6 }}>{t("common.delete", "DELETE")}</span>
                </button>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div data-testid="events-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="page-title">{t("events.title")}</h1>
          <p className="page-subtitle">{t("events.lodge_meetings_ceremonies_and_gatherings")}</p>
        </div>
        {isAdmin && (
          <button className="btn-primary-gold flex items-center gap-2" onClick={openCreate} data-testid="add-event-btn">
            <Plus size={14} /> {t("events.newEvent")}
          </button>
        )}
      </div>

      <div className="gold-divider my-8" />

      {error && <div className="error-banner mb-4">{error}</div>}

      <section>
        <h2 className="section-heading">{t("events.upcoming")}</h2>
        <div className="gold-divider mt-2 mb-4" />
        {upcoming.length === 0 ? <p style={{ fontStyle: "italic", color: "#858481" }}>{t("events.noUpcoming")}</p> : <div className="space-y-3">{upcoming.map(renderEvent)}</div>}
      </section>

      {past.length > 0 && (
        <section className="mt-10">
          <h2 className="section-heading">{t("events.past")}</h2>
          <div className="gold-divider mt-2 mb-4" />
          <div className="space-y-3 opacity-70">{past.slice(0, 30).map(renderEvent)}</div>
        </section>
      )}

      {showForm && (
        <div className="dialog-overlay" onClick={() => setShowForm(false)}>
          <form className="dialog-panel" onClick={(e) => e.stopPropagation()} onSubmit={save} data-testid="event-form">
            <div className="flex items-center justify-between mb-4">
              <h2 className="dialog-title" style={{ margin: 0 }}>{editing ? t("events.editEvent") : t("events.newEvent")}</h2>
              <button type="button" onClick={() => setShowForm(false)}><X size={20} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="label-lodge">{t("events.formTitle", "Titlu eveniment")} *</label>
                <input className="input-lodge" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-lodge">{t("events.eventDate")} *</label>
                  <input type="datetime-local" className="input-lodge" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                </div>
                <div>
                  <label className="label-lodge">{t("events.eventType")}</label>
                  <select className="input-lodge" value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })}>
                    {EVENT_TYPES.map((et) => <option key={et} value={et}>{t(`events.types.${et}`, et)}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="label-lodge">{t("events.location")} *</label>
                <input className="input-lodge" required value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
              </div>
              <div>
                <label className="label-lodge">{t("events.description")}</label>
                <textarea rows={4} className="input-lodge" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div data-testid="event-visibility-section">
                <label className="label-lodge">{t("events.visibilityLabel")} *</label>
                <div className="flex flex-col gap-1 py-1">
                  {[
                    ["lodge", t("events.visibilityLodge")],
                    ["selected", t("events.visibilitySelected")],
                    ["obedience", t("events.visibilityObedience")],
                  ].map(([val, label]) => (
                    <label key={val} className="flex items-center gap-2" data-testid={`event-visibility-${val}`}>
                      <input
                        type="radio"
                        name="event-visibility"
                        checked={form.visibility === val}
                        onChange={() => setForm({ ...form, visibility: val })}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
                {form.visibility === "selected" && (
                  <div style={{ maxHeight: 140, overflowY: "auto", border: "1px solid var(--cream-200)", padding: "0.5rem" }} data-testid="event-visible-lodges-list">
                    {childLodges.filter((l) => l.id !== user?.child_lodge_id).length === 0 ? (
                      <div className="kv-label">{t("events.visibilityNoLodges")}</div>
                    ) : (
                      childLodges.filter((l) => l.id !== user?.child_lodge_id).map((l) => (
                        <label key={l.id} className="flex items-center gap-2 py-1">
                          <input
                            type="checkbox"
                            checked={form.visible_lodge_ids.includes(l.id)}
                            onChange={() => setForm({
                              ...form,
                              visible_lodge_ids: form.visible_lodge_ids.includes(l.id)
                                ? form.visible_lodge_ids.filter((x) => x !== l.id)
                                : [...form.visible_lodge_ids, l.id],
                            })}
                            data-testid={`event-visible-lodge-${l.id}`}
                          />
                          <span>{l.name}</span>
                        </label>
                      ))
                    )}
                  </div>
                )}
              </div>
              <div>
                <label className="label-lodge">{t("events.invitedMembers")}</label>
                <div style={{ maxHeight: 180, overflowY: "auto", border: "1px solid var(--cream-200)", padding: "0.5rem" }}>
                  {members.length === 0 ? (
                    <div className="kv-label">{t("members.noResults")}</div>
                  ) : (
                    members.map((m) => (
                      <label key={m.id} className="flex items-center gap-2 py-1">
                        <input type="checkbox" checked={form.invited_members.includes(m.id)} onChange={() => toggleMember(m.id)} />
                        <span>{m.first_name} {m.last_name} <span className="kv-label" style={{ marginLeft: 6 }}>{m.grade}</span></span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button type="button" className="btn-outline-gold" onClick={() => setShowForm(false)}>{t("common.cancel")}</button>
              <button type="submit" className="btn-primary-gold" disabled={saving}>{saving ? "..." : t("common.save")}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
